
import React from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CtaSection from '../../components/feature/CtaSection';
import { Link } from 'react-router-dom';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

export default function Services() {
  useSEO({
    title: '診療案内｜おかやま小児科クリニック｜岡山市の小児科・予防接種・乳幼児健診',
    description:
      '岡山市北区のおかやま小児科クリニックの診療案内。一般小児科診療、予防接種、乳幼児健診、アレルギー疾患（喘息・アトピー・食物アレルギー）の診断と治療。診療時間9:00-18:00、予約優先制。',
    keywords:
      '岡山市,小児科,診療案内,予防接種,乳幼児健診,アレルギー,喘息,アトピー,食物アレルギー,診療時間',
    canonicalPath: '/services',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema(
          '診療案内',
          '一般小児科診療、予防接種、乳幼児健診、アレルギー疾患の診療内容と診療時間',
          '/services'
        ),
        {
          '@type': 'MedicalProcedure',
          name: '小児科診療',
          procedureType: '一般診療・予防接種・乳幼児健診・アレルギー疾患',
          description: '岡山市の小児科専門医による総合的な小児医療サービス',
        },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[25vh] sm:h-[30vh] flex items-center justify-center overflow-hidden mt-20 bg-gradient-to-r from-[#F48FB1] via-[#D4A0D0] to-[#A78BFA]">
        <div className="relative z-10 text-center text-white px-4">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">診療案内</h2>
          <p className="text-base sm:text-lg md:text-xl">岡山市のお子様の健康をトータルサポート</p>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="space-y-12 sm:space-y-16">
            {/* Service 1 */}
            <div className="bg-gradient-to-br from-[#FCE4EC]/50 to-[#FFFDE7]/40 rounded-3xl shadow-xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="h-64 sm:h-80 lg:h-auto">
                  <img
                    src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/71efe38f63a30cda1ef1200276903102.jpeg"
                    alt="一般小児科診療"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8 sm:p-12">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#F48FB1] rounded-full flex items-center justify-center mb-6">
                    <i className="ri-stethoscope-line text-3xl sm:text-4xl text-white"></i>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-6">
                    一般小児科診療
                  </h3>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                    風邪、発熱、咳、鼻水、腹痛、下痢、嘔吐など、お子様の日常的な体調不良に対応いたします。
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                    岡山市で25年以上の診療経験を持つ院長が、お子様の症状を丁寧に診察し、適切な治療を行います。
                  </p>
                  <ul className="space-y-2 text-sm sm:text-base text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#F48FB1] text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                      <span>発熱・風邪症状の診察と治療</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#F48FB1] text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                      <span>感染症の診断と治療</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#F48FB1] text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                      <span>消化器症状の診察</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#F48FB1] text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                      <span>皮膚疾患の診察と治療</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Service 2 */}
            <div className="bg-gradient-to-br from-[#FFFDE7]/60 to-[#FCE4EC]/40 rounded-3xl shadow-xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="h-64 sm:h-80 lg:h-auto lg:order-2">
                  <img
                    src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/c547d32f188db14e221508e9262b16e2.webp"
                    alt="予防接種"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8 sm:p-12 lg:order-1">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#FFD54F] rounded-full flex items-center justify-center mb-6">
                    <i className="ri-syringe-line text-3xl sm:text-4xl text-white"></i>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-6">
                    予防接種
                  </h3>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                    生後2ヶ月からの定期接種・任意接種に対応しております。岡山市の予防接種助成制度もご利用いただけます。
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                    お子様の月齢に合わせた最適な接種スケジュールをご提案いたします。
                  </p>
                  <ul className="space-y-2 text-sm sm:text-base text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#FFD54F] text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                      <span>ヒブワクチン、小児用肺炎球菌ワクチン</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#FFD54F] text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                      <span>B型肝炎、ロタウイルスワクチン</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#FFD54F] text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                      <span>四種混合、MR（麻疹・風疹）ワクチン</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#FFD54F] text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                      <span>インフルエンザワクチン</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Service 3 */}
            <div className="bg-gradient-to-br from-[#EDE7F6]/40 to-[#FCE4EC]/30 rounded-3xl shadow-xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="h-64 sm:h-80 lg:h-auto">
                  <img
                    src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/befb0f5ee5d966d16018fcd802a56d31.webp"
                    alt="乳幼児健診"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8 sm:p-12">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#A78BFA] rounded-full flex items-center justify-center mb-6">
                    <i className="ri-heart-add-line text-3xl sm:text-4xl text-white"></i>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-6">
                    乳幼児健診
                  </h3>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                    お子様の成長発達を定期的にチェックし、健やかな成長をサポートいたします。
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                    岡山市の乳幼児健診制度に対応し、育児相談も承っております。
                  </p>
                  <ul className="space-y-2 text-sm sm:text-base text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#A78BFA] text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                      <span>3〜4ヶ月健診、6〜7ヶ月健診</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#A78BFA] text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                      <span>9〜10ヶ月健診、1歳6ヶ月健診</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#A78BFA] text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                      <span>3歳児健診、就学前健診</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#A78BFA] text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                      <span>発達相談、育児相談</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Service 4 */}
            <div className="bg-gradient-to-br from-[#FCE4EC]/40 to-[#EDE7F6]/30 rounded-3xl shadow-xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="h-64 sm:h-80 lg:h-auto lg:order-2">
                  <img
                    src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/f2f4a2ea0da9ca50f718a677eb24db89.jpeg"
                    alt="アレルギー疾患"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8 sm:p-12 lg:order-1">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#F48FB1] rounded-full flex items-center justify-center mb-6">
                    <i className="ri-lungs-line text-3xl sm:text-4xl text-white"></i>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-6">
                    アレルギー疾患
                  </h3>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                    気管支喘息、アトピー性皮膚炎、食物アレルギー、花粉症など、小児アレルギー疾患の診断と治療を行います。
                  </p>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                    岡山市の小児アレルギー専門医として、適切な検査と治療をご提供いたします。
                  </p>
                  <ul className="space-y-2 text-sm sm:text-base text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#F48FB1] text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                      <span>気管支喘息の診断と治療</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#F48FB1] text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                      <span>アトピー性皮膚炎のスキンケア指導</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#F48FB1] text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                      <span>食物アレルギーの検査と指導</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-[#F48FB1] text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                      <span>アレルギー性鼻炎の治療</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* 診療の流れ */}
          <div className="mt-16 sm:mt-24">
            <div className="text-center mb-12 sm:mb-16">
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4">診療の流れ</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Step 1 */}
              <div className="text-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-[#FCE4EC] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl sm:text-4xl font-bold text-[#F48FB1]">1</span>
                </div>
                <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-3">受付</h4>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  マイナンバーカード・医療証をお持ちください
                </p>
              </div>

              {/* Step 2 */}
              <div className="text-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-[#EDE7F6] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl sm:text-4xl font-bold text-[#A78BFA]">2</span>
                </div>
                <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-3">問診票記入</h4>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  症状・気になることをご記入ください
                </p>
              </div>

              {/* Step 3 */}
              <div className="text-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-[#FFFDE7] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl sm:text-4xl font-bold text-[#FFD54F]">3</span>
                </div>
                <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-3">診察</h4>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  丁寧な診察とわかりやすい説明
                </p>
              </div>

              {/* Step 4 */}
              <div className="text-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-[#E0F2F1] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl sm:text-4xl font-bold text-[#81C784]">4</span>
                </div>
                <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-3">お会計</h4>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  次回のご予約も承ります
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CtaSection />

      <Footer />
    </div>
  );
}
